import React, { useEffect, useState } from "react";

function getYouTubeVideoId(url) {
    try {
        const u = new URL(url);
        if (u.hostname === "youtu.be") {
            return u.pathname.slice(1);
        }
        if (u.hostname.includes("youtube.com")) {
            if (u.pathname.startsWith("/embed/")) return u.pathname.split("/")[2] || "";
            const idFromV = u.searchParams.get("v");
            if (idFromV) return idFromV;
        }
        return "";
    } catch (e) {
        console.error("Failed to parse video URL", { url, error: e });
        return "";
    }
}

export function Sidebar({ selectedPoint, onClose, sidebarData }) {
    const isMobile = window.innerWidth < 768;
    const defaultImage = "https://admin.greenheroes.co/storage/files/2/att.IRrQNoc1FVnAVbqjjfW7nojRSdwdAgsWV_2fr3nq7A0.jpg";
    const systemOn = sidebarData?.system_status;

    const [shouldShowVideo, setShouldShowVideo] = useState(Boolean(selectedPoint?.sidebarVideo));
    useEffect(() => {
        setShouldShowVideo(Boolean(selectedPoint?.sidebarVideo));
    }, [selectedPoint?.sidebarVideo]);

    if (!selectedPoint) return null; // Don't render the sidebar if no point is selected

    const videoUrl = selectedPoint?.sidebarVideo || "";
    const youTubeId = videoUrl ? getYouTubeVideoId(videoUrl) : "";
    const isYouTube = Boolean(youTubeId);
    const youTubeEmbedUrl = isYouTube
        ? `https://www.youtube.com/embed/${youTubeId}?autoplay=1&mute=1&loop=1&playlist=${youTubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`
        : "";

    return (
        <div
            style={{
                ...styles.sidebar,
                left: isMobile ? 0 : "unset",
                width: isMobile ? "unset" : "50vw",
                background: isMobile ? "rgba(0, 0, 0, 1)" : "rgba(0, 0, 0, 0.7)",
            }}
        >
            <button onClick={onClose} style={styles.closeButton}>
                ✖
            </button>
            <div style={{ justifySelf: "center" }}>
                {shouldShowVideo && videoUrl ? (
                    isYouTube ? (
                        <iframe
                            title={selectedPoint?.sidebarName || "sidebar-video"}
                            src={youTubeEmbedUrl}
                            style={styles.video}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen={false}
                        />
                    ) : (
                        <video
                            src={videoUrl}
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            poster={selectedPoint.sidebarImage || defaultImage}
                            style={styles.video}
                            onError={(e) => {
                                console.error('Sidebar video failed to load', {
                                    error: e,
                                    src: videoUrl,
                                });
                                setShouldShowVideo(false);
                            }}
                        />
                    )
                ) : (
                    <img src={selectedPoint.sidebarImage || defaultImage} alt={selectedPoint?.sidebarName || ""} style={styles.image} />
                )}
            </div>
            <InfoCard>
                <InfoRow label="Name" value={selectedPoint.sidebarName} />
                <InfoRow
                    label="Location"
                    value={selectedPoint.location}
                />
            </InfoCard>
            <InfoCard style={systemOn ? { opacity: 1 } : { opacity: 0.5 }}>
                <InfoRow label="System" value={sidebarData?.system_status ? "ON" : "OFF"} />
                <InfoRow label="Status Battery" value={sidebarData?.battery_status ? "ON" : "OFF"} />
                <InfoRow label="Today Yield" value={(sidebarData?.today_yeild ?? 0) + " kW/h"} />
                <InfoRow label="CO2 Saving" value={(sidebarData?.co2_saving ?? 0) + " tons"} />
                <InfoRow label="Battery" value={sidebarData?.battery_status ? "ON" : "OFF"} />
                <InfoRow label="Dryer" value={sidebarData?.dryer_status ? "ON" : "OFF"} />
            </InfoCard>
        </div>
    );
}

const InfoCard = ({ title, children, style = {} }) => (
    <div style={{ ...styles.cardContainer, ...style }}>
        {title && <h3 style={styles.cardTitle}>{title}</h3>}
        {children}
    </div>
);

const InfoRow = ({ label, value }) => (
    <div style={styles.infoRow}>
        <span style={styles.label}>{label}:</span>
        <span style={styles.value}>{value}</span>
    </div>
);

const styles = {
    sidebar: {
        position: "absolute",
        top: 0,
        right: 0,
        height: "100dvh",
        color: "#fff",
        padding: "0 20px",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
        transform: "translateX(0)",
        transition: "transform 0.3s ease-in-out",
        overflowY: "auto",
        wordWrap: "break-word",
    },
    closeButton: {
        position: "absolute",
        top: "10px",
        right: "10px",
        background: "none",
        color: "#fff",
        border: "none",
        fontSize: "18px",
        cursor: "pointer",
    },
    image: {
        maxWidth: "100%",
        maxHeight: "400px",
        // minHeight: "400px",
        objectFit: "cover",
        borderRadius: "8px",
        marginTop: "10px",
    },
    video: {
        maxWidth: "100%",
        maxHeight: "400px",
        width: "100%",
        aspectRatio: "16 / 9",
        objectFit: "cover",
        borderRadius: "8px",
        marginTop: "10px",
        backgroundColor: "#000",
    },
    cardContainer: {
        display: "flex",
        flexDirection: "column",
        padding: "20px",
        borderStyle: "solid",
        borderRadius: "10px",
        borderWidth: "thin",
        marginTop: "25px",
        background: "#222",
    },
    cardTitle: {
        margin: 0,
        marginBottom: "10px",
        color: "#fff",
    },
    infoRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid #444",
    },
    label: {
        fontWeight: "bold",
    },
    value: {
        paddingLeft: "25px",
        textAlign: "right",
    },
};
